import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';

// Get all Material Icons (excluding internal/utility icons)
const iconNames = Object.keys(MuiIcons).filter(
  (name) =>
    name !== 'default' &&
    !name.endsWith('Outlined') &&
    !name.endsWith('Rounded') &&
    !name.endsWith('Sharp') &&
    !name.endsWith('TwoTone') &&
    !name.includes('Create')
);

// Popular icons to show first
const popularIcons = [
  'Star', 'Favorite', 'ThumbUp', 'EmojiEvents', 'SportsFootball',
  'SportsBasketball', 'SportsHockey', 'SportsSoccer', 'SportsBaseball',
  'Person', 'Face', 'Pets', 'Bolt', 'Whatshot', 'LocalFireDepartment',
  'Diamond', 'Casino', 'AttachMoney', 'Celebration', 'MilitaryTech',
  'Psychology', 'Rocket', 'AutoAwesome', 'Verified', 'Grade',
  'Crown', 'Shield', 'Gavel', 'WorkspacePremium', 'Stars',
];

// eslint-disable-next-line react-refresh/only-export-components
export const renderIcon = (name: string, size: number = 24) => {
  const IconComponent = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name];
  if (!IconComponent) return null;
  return <IconComponent sx={{ fontSize: size }} />;
};

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  size?: 'small' | 'medium';
}

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Icon',
  helperText = 'Icon shown on squares (overrides handle)',
  size = 'medium',
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState('');

  const filteredIcons = useMemo(() => {
    const searchLower = iconSearch.toLowerCase();
    if (!searchLower) {
      // Show popular icons first, then others
      const others = iconNames.filter((name) => !popularIcons.includes(name));
      return [...popularIcons.filter((name) => iconNames.includes(name)), ...others];
    }
    return iconNames.filter((name) => name.toLowerCase().includes(searchLower));
  }, [iconSearch]);

  const handleOpenDialog = () => {
    setIconSearch('');
    setDialogOpen(true);
  };

  const handleSelectIcon = (name: string) => {
    onChange(name);
    setDialogOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <>
      <Box>
        <Button
          variant="outlined"
          onClick={handleOpenDialog}
          disabled={disabled}
          startIcon={value ? renderIcon(value) : undefined}
          endIcon={value ? (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ ml: -0.5, mr: -1 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : undefined}
          sx={{
            height: size === 'small' ? 40 : 56,
            width: '100%',
            justifyContent: value ? 'center' : 'flex-start',
            minWidth: 0,
            fontSize: size === 'small' ? '0.8125rem' : undefined,
          }}
        >
          {value ? null : `Choose ${label}`}
        </Button>
        {helperText && (
          <Box sx={{ mt: 0.5, ml: 1.75, fontSize: '0.75rem', color: 'text.secondary' }}>
            {helperText}
          </Box>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Choose an Icon</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search icons..."
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
              gap: 1,
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            {filteredIcons.slice(0, 200).map((name) => (
              <Tooltip key={name} title={name}>
                <IconButton
                  onClick={() => handleSelectIcon(name)}
                  sx={{
                    border: value === name ? 2 : 1,
                    borderColor: value === name ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 1,
                    position: 'relative',
                  }}
                >
                  {renderIcon(name, 28)}
                  {value === name && (
                    <CheckIcon
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        fontSize: 12,
                        color: 'primary.main',
                      }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          {filteredIcons.length > 200 && (
            <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
              Showing first 200 results. Use search to find more icons.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
